import React from 'react';
import { Check, SpinnerGap, Circle } from '@phosphor-icons/react';
import { ContractText } from '@/components/system/ContractUI';

/**
 * ProcessingStatusPanel - Generic Vertical Stepper
 * Authority: Phase 4 Deliverable #5
 *
 * Accepts a config prop with steps array. Each step has:
 *   { id: string, label: string, status: 'pending' | 'active' | 'completed' | 'error' }
 *
 * Renders a vertical stepper with business-facing labels and animations.
 * Generic component — not article-specific. Any agent can use this.
 */

const STATUS_ICON = {
    completed: (
        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
            <Check weight="bold" className="w-4 h-4 text-emerald-600" />
        </div>
    ),
    active: (
        <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center shrink-0 animate-pulse">
            <SpinnerGap weight="bold" className="w-4 h-4 text-brand-600 animate-spin" />
        </div>
    ),
    error: (
        <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center shrink-0">
            <Circle weight="fill" className="w-3 h-3 text-red-500" />
        </div>
    ),
    pending: (
        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
            <Circle weight="regular" className="w-3 h-3 text-slate-400" />
        </div>
    ),
};

const STATUS_LABEL_STYLE = {
    completed: 'text-emerald-700 font-semibold',
    active: 'text-brand-700 font-semibold',
    error: 'text-red-600 font-semibold',
    pending: 'text-slate-400',
};

export function ProcessingStatusPanel({ steps = [], title }) {
    if (steps.length === 0) return null;

    return (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            {title && (
                <ContractText variant="sectionLabel" className="mb-4">
                    {title}
                </ContractText>
            )}
            <div className="space-y-0">
                {steps.map((step, index) => {
                    const isLast = index === steps.length - 1;
                    const icon = STATUS_ICON[step.status] || STATUS_ICON.pending;
                    const labelStyle = STATUS_LABEL_STYLE[step.status] || STATUS_LABEL_STYLE.pending;

                    return (
                        <div key={step.id} className="flex gap-3">
                            {/* Icon + Connector Line */}
                            <div className="flex flex-col items-center">
                                {icon}
                                {!isLast && (
                                    <div className={`w-0.5 flex-1 min-h-[24px] ${
                                        step.status === 'completed' ? 'bg-emerald-200' : 'bg-slate-200'
                                    }`} />
                                )}
                            </div>

                            {/* Label */}
                            <div className="pb-4">
                                <p className={`text-base ${labelStyle}`}>
                                    {step.label}
                                </p>
                                {step.detail && (
                                    <p className="text-sm text-slate-500 mt-0.5">
                                        {step.detail}
                                    </p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
